import { useEffect, useMemo, useState } from "react";
import { environments } from "../util/saveAPI";
import { getItems, removeItems } from "./utils/cartAPI";

export const uuidv4 = () =>
  ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );

export const isEmpty = (obj) => {
  for (var key in obj) {
    if (obj.hasOwnProperty(key)) return false;
  }
  return true;
};

export const addItemsToCart = async (items) => {
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  };
  const response = await fetch(`/cart/add.js`, options);
  if (response.status === 200) {
    return response;
  }
  throw new Error(
    `Failed to add items to cart, Shopify returned ${response.status} ${response.statusText}`
  );
};

export const removeExistingItems = async (timestamp) => {
  const cart = await getItems();
  const matchingItems =
    timestamp && cart?.items
      ? cart.items.filter((item) => {
          return parseInt(item?.properties?._Timestamp) === parseInt(timestamp);
        })
      : [];

  if (!matchingItems?.length) return;
  const keys = matchingItems.map((x) => x.key);
  return await removeItems(keys);
};

/**
 *
 * @param {Object} props
 * @param {Object} props.bounds
 * @param {Object} props.bounds.top_left
 * @param {Number} props.bounds.top_left.x
 * @param {Number} props.bounds.top_left.y
 * @param {Object} props.bounds.bottom_right
 * @param {Number} props.bounds.bottom_right.x
 * @param {Number} props.bounds.bottom_right.y
 * @param {Number} props.bound_height
 * @param {Number} props.monogram_height
 */
export const useMonogramBounds = ({
  bounds: _bounds = {
    top_left: {
      x: 0,
      y: 0,
    },
    bottom_right: {
      x: 0,
      y: 0,
    },
  },
  bound_height = 12,
  monogram_height = 1,
  calibrating,
}) => {
  const { x: topLeftX, y: topLeftY } = _bounds?.top_left || { x: 30, y: 30 };
  const { x: bottomRightX, y: bottomRightY } = _bounds?.bottom_right || { x: 70, y: 70 };

  const [tempBounds, setTempBounds] = useState({
    top_left: { x: topLeftX, y: topLeftY },
    bottom_right: { x: bottomRightX, y: bottomRightY },
  });

  const bounds = calibrating ? tempBounds : _bounds;

  const boundHeight = bound_height || 12;
  const monogramHeight = monogram_height || 1;

  const monogramHeightPercent = (monogramHeight / boundHeight) * 100;
  const boundsFromTop = bounds.top_left.y;
  const boundsFromLeft = bounds.top_left.x;
  const boundsWidth = bounds.bottom_right.x - bounds.top_left.x;
  const boundsHeight = bounds.bottom_right.y - bounds.top_left.y;

  return {
    styles: {
      monogram: {
        height: `${monogramHeightPercent}%`,
      },
      bounds: {
        border: calibrating ? "1px dotted black" : "none",
        top: `${boundsFromTop}%`,
        left: `${boundsFromLeft}%`,
        width: `${boundsWidth}%`,
        height: `${boundsHeight}%`,
      },
    },
    heightPercent: monogramHeightPercent,
    tempBounds,
    setTempBounds,
  };
};

export const useSVGBounds = ({ chosenFont, font_height, calibrating }) => {
  const defaultFontPosition = chosenFont.decorative_svg_bounding_box
    ? JSON.parse(chosenFont.decorative_svg_bounding_box)
    : null;

  const chosenFontSettings = chosenFont?.font_settings
    ? JSON.parse(chosenFont?.font_settings)
    : null;

  const _bounds = !isEmpty(chosenFontSettings?.decorative_svg_bounding_box)
    ? chosenFontSettings.decorative_svg_bounding_box
    : !isEmpty(defaultFontPosition)
    ? defaultFontPosition
    : {
        axis: {
          x: 0,
          y: 0,
        },
        width: 50,
        height: 50,
      };

  const { x: xAxis, y: yAxis } = _bounds?.axis || { x: 30, y: 30 };
  const svgWidth = _bounds?.width || 50;
  const svgHeight = _bounds?.height || 50;

  const [tempBounds, setTempBounds] = useState({
    axis: { x: xAxis, y: yAxis },
    width: svgWidth,
    height: svgHeight,
  });

  useEffect(() => {
    setTempBounds({
      axis: { x: xAxis, y: yAxis },
      width: svgWidth,
      height: svgHeight,
    });
  }, [chosenFont]);

  const bounds = calibrating ? tempBounds : _bounds;

  const boundHeight = chosenFont?.decorative_svg_height
    ? Number(chosenFont.decorative_svg_height)
    : 12;
  const fontHeight = font_height || 1;

  const svgHeightPercent = (fontHeight / boundHeight) * 100;
  const boundsFromTop = bounds.axis.y;
  const boundsFromLeft = bounds.axis.x;
  const boundsWidth = bounds.width;
  const boundsHeight = bounds.height;

  return {
    styles: {
      bounds: {
        top: `${boundsFromTop}%`,
        left: `${boundsFromLeft}%`,
        width: `${boundsWidth}%`,
        height: `${boundsHeight}%`,
      },
    },
    heightPercent: svgHeightPercent,
    tempBounds,
    setTempBounds,
  };
};

/**
 * Adjusts a string to be a certain length, truncating at the last non-dot character while
 * preserving dots to not count towards the length
 *
 * Thanks chatgpt
 *
 * @param {String} str string to truncate
 * @param {Number} n length of string before truncation
 * @returns
 */
export const trimStringWithException = (str, n, specialCharacter, maxPeriods) => {
  let dotCount = 0;
  let nonDotCount = 0;

  for (let i = 0; i < str.length; i++) {
    if (str[i] === specialCharacter) {
      dotCount++;

      // Check if the dot count exceeds the maximum allowed periods
      if (dotCount > maxPeriods) {
        // Adjust the string by slicing it at the previous index and return the result
        return str.slice(0, i);
      }
    } else {
      nonDotCount++;
    }

    if (nonDotCount === n && i !== str.length - 1 && str[i + 1] !== ".") {
      return str.slice(0, i + 1);
    }
  }

  return str;
};

export const getRegexForAllowedCharacters = (
  allowedCharacters,
  specialCharacter,
  allowedSpecialCharacters
) => {
  const sp = specialCharacter ? `\\${specialCharacter}` : "";
  const asp = allowedSpecialCharacters ? allowedSpecialCharacters : "";

  switch (allowedCharacters) {
    case "Alpha":
      return new RegExp(`[^a-zA-Z${sp}${asp}]*`, "g");
    case "Alphanumeric":
      return new RegExp(`[^a-zA-Z0-9${sp}${asp}]*`, "g");
    case "Numeric":
      return new RegExp(`[^0-9${sp}${asp}]*`, "g");
    default:
      return false;
  }
};

export const getParamFromUrl = (value) => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(value);
};

export function getResizedSwatch(src, size = 96) {
  if (!src) return "";

  try {
    const url = new URL(src, window.location.origin);

    const isClassicShopifyCDN = url.hostname.includes("cdn.shopify.com");
    const isFilesURL = url.pathname.includes("/cdn/shop/files/");

    if (isClassicShopifyCDN) {
      // Insert `_96x` before file extension
      return src.replace(/(\.(jpg|jpeg|png|gif|webp))(\?.*)?$/, `_${size}x$1`);
    }

    if (isFilesURL) {
      url.searchParams.set("width", size.toString());
      url.searchParams.delete("height");
      return url.href;
    }

    if (url.hostname.includes("accentuate.io")) {
      // Accentuate: add ?options=w96
      url.searchParams.set("options", `w${size}`);
      return url.href;
    }

    // Unknown image source
    return src;
  } catch {
    return src; // Fallback if it's not a valid URL
  }
}
